import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(2deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const numberAnimation = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
    opacity: 0.03;
  }
  50% {
    transform: scale(1.1) rotate(2deg);
    opacity: 0.05;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.03;
  }
`;

export const ContactContainer = styled.div`
  min-height: 100vh;
  background: #fff;
  padding: 2rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: 0;
    right: 0;
    height: 100vh;
    background: #fff;
    transform: skewY(-6deg);
    transform-origin: top left;
    z-index: 0;
  }
`;

export const BackgroundNumber = styled.div`
  position: fixed;
  bottom: -1rem;
  left: 2rem;
  font-size: 8vw;
  font-weight: bold;
  color: #fe0000;
  opacity: 0.03;
  pointer-events: none;
  z-index: 0;
  font-family: 'Permanent Marker', cursive;
  animation: ${numberAnimation} 10s ease-in-out infinite;
  white-space: nowrap;
  transform-origin: bottom left;
`;

export const ContactContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  color: #000;
  animation: ${fadeIn} 0.6s ease forwards;
  position: relative;
  z-index: 2;

  h1 {
    font-size: 3.5rem;
    font-weight: 500;
    margin-bottom: 1rem;
    color: #000;
  }

  p {
    font-size: 1.2rem;
    opacity: 0.9;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
    color: #666;
  }
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  margin-top: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

export const ContactInfo = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: 0.2s;
  opacity: 0;
`;

export const InfoItem = styled.div`
  margin-bottom: 1.4rem;

  h3 {
    font-size: 1.2rem;
    font-weight: 500;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      font-size: 1.4rem;
    }
  }

  p {
    color: #666;
    line-height: 1.6;
    font-size: 1.1rem;
  }

  a {
    color: #666;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #000;
    }
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

export const SocialButton = styled.a`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: #000;
    color: white;
    transform: translateY(-3px);
  }

  svg {
    font-size: 1.2rem;
  }
`;

export const ContactForm = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: 0.4s;
  opacity: 0;
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
  position: relative;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f8f8f8;

  &:focus {
    border-color: #000;
    background: white;
    outline: none;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 150px;
  transition: all 0.3s ease;
  background: #f8f8f8;
  resize: vertical;

  &:focus {
    border-color: #000;
    background: white;
    outline: none;
  }
`;

export const SubmitButton = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 1.2rem;
  }
`;

export const FloatingShape = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  background: linear-gradient(45deg, rgba(254, 0, 0, 0.15), rgba(254, 0, 0, 0.05));
  border-radius: 50%;
  animation: ${float} 6s ease-in-out infinite;

  &.shape1 {
    top: 10%;
    right: 5%;
    animation-delay: 0s;
  }

  &.shape2 {
    bottom: 10%;
    left: 5%;
    width: 200px;
    height: 200px;
    animation-delay: 2s;
  }
`;

export const SuccessMessage = styled.div`
  background: #4CAF50;
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: ${fadeIn} 0.3s ease forwards;

  svg {
    font-size: 1.2rem;
  }
`; 